<template>
  <div class="sign-up-component-container">
    <div>
      <div class="heading">
        <img
          v-if="certApp"
          class="ase-logo"
          src="../../assets/images/ase/logo1.svg"
          alt=""
          role="presentation"
        >
        <img
          v-else
          :src="appLogo"
          alt=""
          role="presentation"
        >
        <br>Create Account
      </div>
      <facebook :create-account="true" />
      <div>
        <label
          class="title"
          for="first-name"
        >
          First Name
        </label>
        <label
          v-if="fieldError('firstName')"
          class="error"
        >
          {{ errors.firstName }}
        </label>
      </div>
      <input
        id="first-name"
        v-model="firstName"
        type="text"
        @keyup.enter="submit"
      >
      <template v-if="lastNameRequired">
        <div>
          <label
            class="title"
            for="last-name"
          >
            Last Name
          </label>
          <label
            v-if="fieldError('lastName')"
            class="error"
          >
            {{ errors.lastName }}
          </label>
        </div>
        <input
          id="last-name"
          v-model="lastName"
          type="text"
          @keyup.enter="submit"
        >
      </template>
      <div>
        <label
          class="title"
          for="email"
        >
          Email
        </label>
        <label
          v-if="fieldError('email')"
          class="error"
        >
          {{ errors.email }}
        </label>
      </div>
      <input
        id="email"
        v-model="email"
        type="text"
        @keyup.enter="submit"
      >
      <div>
        <label
          class="title"
          for="password"
        >
          Password
          <tooltip
            v-if="certApp"
            :message="'Choose a password that is unique to your myASE Renewal account.'"
            :placement="'right'"
          >
            <icon
              name="info"
              variant="7f8fa4"
            />
          </tooltip>
        </label>
        <label
          v-if="fieldError('password')"
          class="error"
        >
          {{ errors.password }}
        </label>
      </div>
      <input
        id="password"
        v-model="password"
        type="password"
        @keyup.enter="submit"
      >
      <activation :activators="activators" />
      <div class="bottom-container">
        <button
          :disabled="loading"
          @click="submit()"
        >
          {{ buttonText }}
        </button>
        <div class="disclaimer">
          <span>By signing up you agree to our&nbsp;</span>
          <a
            href="https://builtbyhlt.com/privacy"
            target="_blank"
          >
            Privacy Policy
          </a>
          <span>&nbsp;and&nbsp;</span>
          <router-link :to="{name: 'eula'}">
            End-User License Agreement
          </router-link>
          <span>.</span>
        </div>
        <router-link
          :to="{name: 'signIn'}"
          class="suggestion"
        >
          Already have an account? Log In
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import Activation from '@components/activation/show';
import ActivationMixin from '@/mixins/activation';
import AuthenticationMixin from '@/mixins/authentication';
import Facebook from '@components/singletons/facebook';
import Icon from '@components/singletons/icon';
import Tooltip from '@components/singletons/tooltip';

const ACTIVATION_EVENT = 'activation-code-change';
import { setupChecklist, ONBOARDING_CHECKLIST_KEY } from '@/utils/checklist';

export default {
  components: { Activation, Facebook, Icon, Tooltip },
  mixins: [ActivationMixin, AuthenticationMixin],
  data() {
    return {
      activationCodes: {},
      activationCodesValid: true,
    };
  },
  computed: {
    activators() {
      return this.$store.getters['activation/activators'];
    },
    appLogo() {
      return this.$store.getters['app/appLogo'];
    },
    buttonText() {
      return this.certApp ? 'Let\'s go!' : 'Create Account';
    },
    lastNameRequired() {
      return this.certApp > 0;
    },
  },
  created() {
    if (this.authenticated) {
      this.$router.push({name: 'home'});
    } else {
      this.$eventBus.$on(ACTIVATION_EVENT, this.updateActivationCodes);
    }
  },
  beforeDestroy() {
    this.$eventBus.$off(ACTIVATION_EVENT);
  },
  methods: {
    fieldError(key) {
      return Object.keys(this.errors).includes(key);
    },
    submit() {
      // make sure request isn't in progress already
      if (this.loading) return;

      // clear all validations
      this.clearAuthenticationErrors();

      this.$notify({
        group: 'notifications',
        clean: true,
      });

      const trackingParams = this.collectTrackingParams();

      const params = {
        activationCodes: this.activationCodes,
        user: {
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName,
          password: this.password,
        },
        ...trackingParams,
      };

      this.loading = true;
      this.$store.dispatch('session/signUp', params).then(results => {
        this.$analytics.events.signUp(this.$store, 'Native');
        this.activationCodeNotifications(results);
        // create checklist state here
        setupChecklist(this.$store, ONBOARDING_CHECKLIST_KEY);
        this.postSignUp();
      }).catch(e => {
        this.handleErrors(e, 'Sign Up');
      }).finally(() => {
        this.loading = false;
      });
    },
  },
};
</script>

<style lang='scss' scoped>

  .sign-up-component-container {
    height: 100%;
    padding: rem-calc(20);

    > div {
      margin: auto;
      max-width: rem-calc(400);
    }

    .suggestion {
      color: #354052;
      cursor: pointer;
      display: block;
      font-size: rem-calc(12);
      text-align: center;
      text-decoration: underline;
    }

    /deep/ label {

      &.title {
        color: #7f8fa4;
        font-weight: $semi-bold;
      }

      &.error {
        color: red;
        float: right;
        font-size: rem-calc(12);
      }

      img {
        height: rem-calc(14);
        margin: 0 rem-calc(4);
        width: rem-calc(14);
      }
    }

    input {
      margin: rem-calc(4) 0 rem-calc(12) 0;
      width: 100%;
      border: 1px solid #dfe3e9;
      border-radius: 4px;
      box-sizing: border-box;

      &[type='password'] {
        letter-spacing: 1px;
      }

      &.error {
        border-color: red;
      }
    }

    .heading {
      color: #354052;
      font-size: rem-calc(28);
      font-weight: 600;
      margin-bottom: rem-calc(24);
      text-align: center;

      img {
        max-width: rem-calc(48);

        &.ase-logo {
          margin-bottom: rem-calc(16);
          max-width: rem-calc(96);
          width: 100%;
        }
      }
    }

    button {
      display: block;
      border: none;
      width: 100%;
      background-color: #7bd56e;
      border: 1px solid #65BA59;
      border-radius: 4px;
      color: white;
      margin-bottom: rem-calc(12);
      padding: rem-calc(10);
      text-align: center;

      &:disabled {
        cursor: not-allowed;
        opacity: 0.8;
      }


    }

    .bottom-container {
      margin-top: auto;

      .disclaimer {
        font-size: rem-calc(10);
        margin: rem-calc(4) 0 rem-calc(12) 0;
        text-align: center;
      }
    }
  }
</style>
